import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  changePassword,
  createAdmin,
  disableAdmin,
  disableAgent,
  disableVendor,
  enableVendor,
  getAdminProfile,
  getReferredUsers,
  getRoles,
  getUserById,
  getUsers,
  updateAdminProfile,
  updateUserProfile,
} from "../../apis/admin";
import { toast } from "react-toastify";
import { PaginatedUserResponse, UserProfile } from "./types/adminTypes";
import { cleanObject } from "../../helpers";

// Define the initial state
interface AdminState {
  isLoading: boolean;
  successMessage: string | null;
  errorMessage: string | null;
  users: PaginatedUserResponse | null;
  vendors: any;
  buyers: any;
  ReferredUsers: any;
  adminProfile: UserProfile | null;
  roles: string[];
  selectedUser: any;
}

const initialState: AdminState = {
  isLoading: false,
  successMessage: null,
  errorMessage: null,
  users: null,
  vendors: null,
  buyers: null,
  ReferredUsers: null,
  adminProfile: null,
  roles: [],
  selectedUser: null,
};

// Async thunk for creating an admin
export const createAdminAsync = createAsyncThunk(
  "admin/createAdmin",
  async (signUpData: any, { rejectWithValue }) => {
    try {
      const response = await createAdmin(signUpData);
      signUpData?.role === "COURIER_AGENT" &&
        toast.success(response?.data?.message ?? "Agent added successfully");
      signUpData?.role === "COURIER_ADMIN" &&
        toast.success(
          response?.data?.message ?? "Courier Admin added successfully",
        );
      signUpData?.role === "DISPUTE_ADMIN" &&
        toast.success(
          response?.data?.message ?? "Dispute Admin added successfully",
        );
      signUpData?.role === "INVESTOR" &&
        toast.success(response?.data?.message ?? "Investor added successfully");

      return response; // Adjust based on API response structure
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "An error occurred");
      return rejectWithValue(
        error.response?.data?.message ?? "An error occurred",
      );
    }
  },
);

export const fetchUsersAsync = createAsyncThunk(
  "admin/fetchUsers",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const params = cleanObject(parameters);
      const data = await getUsers(params);
      return { userType: parameters?.userType, res: data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Failed to fetch users");
    }
  },
);

export const fetchReferredUsersAsync = createAsyncThunk(
  "admin/fetchReferredUsers",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const params = cleanObject(parameters);
      const data = await getReferredUsers(params);
      return { userType: parameters?.userType, res: data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Failed to fetch users");
    }
  },
);

export const fetchUserByIdAsync = createAsyncThunk(
  "admin/fetchUserById",
  async (userId: string, { rejectWithValue }) => {
    try {
      const res = await getUserById(userId);
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch user details",
      );
    }
  },
);

export const fetchRolesAsync = createAsyncThunk(
  "admin/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getRoles();
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Failed to fetch users");
    }
  },
);

export const fetchAdminProfileAsync = createAsyncThunk(
  "admin/fetchAdminProfile",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAdminProfile();
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch admin profile",
      );
    }
  },
);
export const updateAdminProfileAsync = createAsyncThunk(
  "admin/updateAdminProfile",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const res = await updateAdminProfile(parameters);
      return res?.data;
    } catch (error: any) {
      toast.error(error.message ?? "Failed to update admin profile");
      return rejectWithValue(
        error.response?.data ?? "Failed to update admin profile",
      );
    }
  },
);
export const updateUserProfileAsync = createAsyncThunk(
  "admin/updateUserProfile",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const res = await updateUserProfile(parameters);
      return res?.data;
    } catch (error: any) {
      toast.error(error.message ?? "Failed to update admin profile");
      return rejectWithValue(
        error.response?.data ?? "Failed to update admin profile",
      );
    }
  },
);
export const disableAgentAsync = createAsyncThunk(
  "admin/disableAgent",
  async (id: any, { rejectWithValue }) => {
    try {
      const res = await disableAgent(id);
      return res?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to disable currier agent",
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to disable currier agent",
      );
    }
  },
);
export const disableAdminAsync = createAsyncThunk(
  "admin/disableAdmin",
  async (id: any, { rejectWithValue }) => {
    try {
      const res = await disableAdmin(id);
      return res?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Failed to disable admin");
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to disable admin",
      );
    }
  },
);

export const disableVendorAsync = createAsyncThunk(
  "admin/disableVendor",
  async (vendorId: string, { rejectWithValue }) => {
    try {
      const res = await disableVendor(vendorId);
      toast.success("Vendor disabled successfully");
      return res?.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ?? "Failed to disable vendor";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  },
);

export const enableVendorAsync = createAsyncThunk(
  "admin/enableVendor",
  async (vendorId: string, { rejectWithValue }) => {
    try {
      const res = await enableVendor(vendorId);
      toast.success("Vendor enabled successfully");
      return res?.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ?? "Failed to enable vendor";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  },
);

export const changeAdminPasswordAsync = createAsyncThunk(
  "admin/changeAdminPassword",
  async ({ parameters, isAuthenticated }: any, { rejectWithValue }) => {
    try {
      const data = await changePassword(parameters);
      toast.success(
        "Password changed successfully, please login again to apply changes",
      );
      if (isAuthenticated) {
        localStorage.clear();
        window.location.replace("/");
      }
      return data;
    } catch (error: any) {
      toast.error(error.message ?? "Failed to change admin password");
      return rejectWithValue(
        error.response?.data ?? "Failed to update admin profile",
      );
    }
  },
);

// Admin slice
const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    resetAdminState: (state) => {
      state.isLoading = false;
      state.successMessage = null;
      state.errorMessage = null;
      state.users = null;
      state.vendors = null;
      state.buyers = null;
    },
    setSelectedUser: (state, action: PayloadAction<any>) => {
      state.selectedUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAdminAsync.pending, (state) => {
        state.isLoading = true;
        state.successMessage = null;
        state.errorMessage = null;
      })
      .addCase(
        createAdminAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.successMessage = "Admin created successfully!";
          state.errorMessage = null;
        },
      )
      .addCase(
        createAdminAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.successMessage = null;
          state.errorMessage = action.payload as string;
        },
      );

    // fetch ReferredUsers
    builder
      .addCase(fetchReferredUsersAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(
        fetchReferredUsersAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.ReferredUsers = action.payload?.res;
        },
      )
      .addCase(fetchReferredUsersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });

    // fetch users
    builder
      .addCase(fetchUsersAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(
        fetchUsersAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.users = action.payload?.res; // Store dispute orders
          state.vendors =
            action.payload.userType === "VENDOR"
              ? action.payload?.res
              : state.vendors;
          state.buyers =
            action.payload.userType === "BUYER"
              ? action.payload?.res
              : state.buyers;
        },
      )
      .addCase(fetchUsersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });

    // fetch admin profile
    builder
      .addCase(fetchAdminProfileAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(
        fetchAdminProfileAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.adminProfile = action.payload; // Store dispute orders
        },
      )
      .addCase(fetchAdminProfileAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    // update admin profile
    builder
      .addCase(updateAdminProfileAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(updateAdminProfileAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateAdminProfileAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    //disable agent
    builder
      .addCase(disableAgentAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(disableAgentAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(disableAgentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    //disable admin
    builder
      .addCase(disableAdminAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(disableAdminAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(disableAdminAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    //update user profile
    builder
      .addCase(updateUserProfileAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(updateUserProfileAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUserProfileAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    // change admin password
    builder
      .addCase(changeAdminPasswordAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(changeAdminPasswordAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changeAdminPasswordAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    //disable vendor
    builder
      .addCase(disableVendorAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(disableVendorAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(disableVendorAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    //enable vendor
    builder
      .addCase(enableVendorAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(enableVendorAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(enableVendorAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    // fetch user by id
    builder
      .addCase(fetchUserByIdAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(
        fetchUserByIdAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.selectedUser = action.payload;
        },
      )
      .addCase(fetchUserByIdAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
      });
    // get admin roles
    builder
      .addCase(fetchRolesAsync.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
        state.roles = [];
      })
      .addCase(
        fetchRolesAsync.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          state.isLoading = false;
          state.roles = action.payload.filter(
            (role) => !["VENDOR", "BUYER"].includes(role),
          );
        },
      )
      .addCase(fetchRolesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload as string;
        state.roles = [];
      });
  },
});

// Export actions and reducer
export const { resetAdminState, setSelectedUser } = adminSlice.actions;
export default adminSlice.reducer;
